<template>
  <div class="referrals-new-report">
    <!-- The -18px this used to carry compensated for the dead space the filter's
         comboboxes reserved below themselves; with that removed the table needs
         real breathing room instead. -->
    <v-row justify="center" class="mt-5 graphContent" style="margin: 0px; margin-bottom: 16px">
      <LatestReportFilter @handleFilter="handleFilter" @exportsToExcel="exportsToExcel" />
    </v-row>

    <v-row justify="end" style="margin: 0px 12px 12px">
      <v-btn
        depressed
        color="primary"
        :loading="exportingAll"
        :disabled="exportingAll"
        @click="exportAllToExcel"
      >
        تصدير الكل إلى Excel
      </v-btn>
    </v-row>

    <v-progress-linear v-if="loading" indeterminate color="#451547" class="mt-2" />

    <div class="referrals-new-classification">
      <div class="rn-table-wrapper" dir="rtl">
        <table class="rn-table">
          <!-- table-layout is fixed, so column widths are taken from the FIRST row
               — which is the group header, and its cells are all colspan'd. Any
               width set on the sub-header cells is ignored and every column comes
               out equal. A colgroup is the only thing fixed layout honours here.
               Widths are the Figma's (1862-5068), and scale proportionally once
               the table is wider than their sum. -->
          <colgroup>
            <col style="width: 241px" /><!-- الحملة -->
            <col style="width: 81px" /><!-- الموقع -->
            <col style="width: 80px" /><!-- المنصة -->
            <col style="width: 86px" /><!-- اول تصنيف: الإيجابي -->
            <col style="width: 82px" /><!-- اول تصنيف: السلبي -->
            <col style="width: 83px" /><!-- اول تصنيف: الجديد -->
            <col style="width: 83px" /><!-- الحالي: الإيجابي -->
            <col style="width: 81px" /><!-- الحالي: السلبي -->
            <col style="width: 82px" /><!-- الحالي: الجديد -->
            <col style="width: 125px" /><!-- المرفوع -->
            <col style="width: 124px" /><!-- المفرغ -->
            <col style="width: 83px" /><!-- المجموع -->
          </colgroup>
          <thead>
            <!-- Group header row (RTL: authored right -> left) -->
            <tr class="rn-group-row">
              <th class="rn-h rn-g-info" colspan="3">معلومات الحمله</th>
              <th class="rn-h rn-g-first" colspan="3">اول تصنيف مكتسب</th>
              <th class="rn-h rn-g-current" colspan="3">التصنيف الحالي للطلب</th>
              <th class="rn-h rn-g-remaining" colspan="2">باقي التصنيفات</th>
              <th class="rn-h rn-g-total" rowspan="2">المجموع</th>
            </tr>
            <!-- Sub-column header row -->
            <tr class="rn-sub-row">
              <th class="rn-h rn-s-info">الحملة</th>
              <th class="rn-h rn-s-info">الموقع</th>
              <th class="rn-h rn-s-info">المنصة</th>
              <th class="rn-h rn-s-first">الإيجابي</th>
              <th class="rn-h rn-s-first">السلبي</th>
              <th class="rn-h rn-s-first">الجديد</th>
              <th class="rn-h rn-s-current">الإيجابي</th>
              <th class="rn-h rn-s-current">السلبي</th>
              <th class="rn-h rn-s-current">الجديد</th>
              <th class="rn-h rn-s-remaining">المرفوع</th>
              <th class="rn-h rn-s-remaining">المفرغ</th>
            </tr>
          </thead>

          <tbody>
            <template v-for="(row, idx) in rows">
              <!-- rn-alt drives the zebra stripe off the DATA index rather than
                   :nth-child, which the interleaved detail rows would break. -->
              <tr
                :key="rowKey(row, idx)"
                class="rn-data-row"
                :class="{ 'rn-alt': idx % 2 === 1 }"
              >
                <!-- معلومات الحمله -->
                <td class="rn-td rn-info-cell rn-name-cell">
                  <div class="rn-campaign">
                    <button
                      v-if="isExpandable(row)"
                      type="button"
                      class="rn-toggle"
                      :aria-expanded="isExpanded(row) ? 'true' : 'false'"
                      @click="toggleRow(row)"
                    >
                      <svg class="rn-caret" width="12" height="8" viewBox="0 0 12 8" aria-hidden="true">
                        <path v-if="isExpanded(row)" d="M6 0L12 8H0z" fill="currentColor" />
                        <path v-else d="M6 8L0 0h12z" fill="currentColor" />
                      </svg>
                      <span class="rn-campaign-name" :title="row.name || ''">{{ shortName(row.name) }}</span>
                    </button>
                    <span v-else class="rn-campaign-name" :title="row.name || ''">{{ shortName(row.name) }}</span>
                  </div>
                </td>
                <td class="rn-td rn-info-cell">
                  <button
                    v-if="siteName(row)"
                    type="button"
                    class="rn-site"
                    @click="openSiteDialog(row)"
                  >{{ siteName(row) }}</button>
                  <span v-else>لا يوجد</span>
                </td>
                <td class="rn-td rn-info-cell">{{ row.platform }}</td>

                <!-- first + current + remaining numeric cells -->
                <td v-for="col in numericColumns" :key="col.c" class="rn-td rn-num-cell">
                  <div class="rn-cell">
                    <div class="rn-line"><span class="rn-count">{{ num(row[col.c]) }}</span><span class="rn-dot"></span></div>
                    <div class="rn-line"><span class="rn-pct">{{ pct(row[col.p]) }}</span><span class="rn-dot"></span></div>
                  </div>
                </td>

                <!-- المجموع — count only. The share percentage is hidden; the empty
                     line keeps the count aligned with the counts beside it. -->
                <td class="rn-td rn-num-cell rn-total-cell">
                  <div class="rn-cell">
                    <div class="rn-line"><span class="rn-count">{{ num(row.sum_of_row) }}</span><span class="rn-dot"></span></div>
                    <div class="rn-line"></div>
                  </div>
                </td>
              </tr>

              <!-- مكرر ثم المجموع. Every figure ships with the row payload, so
                   expanding costs no request. The detail row carries the SAME
                   numericColumns as its parent, so the two stay aligned by
                   construction. -->
              <tr
                v-for="detail in detailRows(row)"
                v-show="isExpanded(row)"
                :key="rowKey(row, idx) + '-' + detail.key"
                class="rn-detail-row"
                :class="{ 'rn-detail-total': detail.key === 'total' }"
              >
                <td class="rn-td rn-detail-label" colspan="3">
                  <div class="rn-detail-fade">
                    {{ detail.label }} : <span class="rn-count">{{ num(detail.total) }}</span>
                  </div>
                </td>

                <td v-for="col in numericColumns" :key="'d-' + col.c" class="rn-td rn-num-cell">
                  <div class="rn-cell rn-detail-fade">
                    <div class="rn-line"><span class="rn-count">{{ num(detail[col.c]) }}</span><span class="rn-dot"></span></div>
                    <div class="rn-line"><span class="rn-pct">{{ pct(detail[col.p]) }}</span><span class="rn-dot"></span></div>
                  </div>
                </td>

                <!-- المجموع — مكرر shows its share of the campaign; the المجموع row
                     itself shows a count only, since a row that is 100% of itself
                     says nothing (and the empty line keeps the counts aligned). -->
                <td class="rn-td rn-num-cell rn-total-cell">
                  <div class="rn-cell rn-detail-fade">
                    <div class="rn-line"><span class="rn-count">{{ num(detail.sum_of_row) }}</span><span class="rn-dot"></span></div>
                    <div v-if="detail.sum_of_row_percentage != null" class="rn-line"><span class="rn-pct">{{ pct(detail.sum_of_row_percentage) }}</span><span class="rn-dot"></span></div>
                    <div v-else class="rn-line"></div>
                  </div>
                </td>
              </tr>
            </template>

            <tr v-if="!rows.length && !loading" class="rn-empty-row">
              <td :colspan="12" class="rn-empty-cell">لا توجد بيانات</td>
            </tr>
          </tbody>

          <!-- Footer: column SUMS (count) + AVERAGE (percentage) -->
          <tfoot v-if="rows.length">
            <tr class="rn-footer-row">
              <td class="rn-td rn-info-cell rn-footer-label" colspan="3">الإجمالي</td>
              <td v-for="col in numericColumns" :key="'f-' + col.c" class="rn-td rn-num-cell">
                <div class="rn-cell">
                  <div class="rn-line"><span class="rn-count">{{ num(footer[col.c]) }}</span><span class="rn-dot"></span></div>
                  <div class="rn-line"><span class="rn-pct">{{ pct(footer[col.p]) }}</span><span class="rn-dot"></span></div>
                </div>
              </td>
              <td class="rn-td rn-num-cell rn-total-cell">
                <div class="rn-cell">
                  <div class="rn-line"><span class="rn-count">{{ num(footer.sum_of_row) }}</span><span class="rn-dot"></span></div>
                  <div class="rn-line"></div>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div class="rn-table-footer">
        <!-- عدد الصفوف في الصفحة الواحدة — commits on blur / Enter rather than on
             every keystroke, so typing "15" does not fire a fetch for "1" first.
             The store is the shared page-size the rest of the admin uses;
             updateCounterOfShow() writes it and refetches the page. -->
        <div class="rn-per-page">
          <label class="rn-per-page-label" for="rn-per-page-input">عدد الصفوف في الصفحة الواحدة</label>
          <input
            id="rn-per-page-input"
            class="rn-per-page-input"
            type="number"
            min="1"
            :max="perPageMax"
            :value="tempCounterOfShow"
            @input="tempCounterOfShow = $event.target.value"
            @blur="updateCounterOfShow"
            @keyup.enter="updateCounterOfShow"
          />
        </div>

        <div v-if="pageCount > 1" class="rn-pagination">
          <v-pagination :value="page" :length="pageCount" total-visible="7" @input="onPageChange" />
        </div>
      </div>
    </div>

    <!-- تفاصيل الموقع — opened from the الموقع cell. Read-only: the campaign is
         edited in the referrals CRUD, this only surfaces where its links point. -->
    <v-dialog v-model="siteDialog" max-width="480px">
      <v-card v-if="siteRow" class="rn-site-card" dir="rtl">
        <v-card-title class="rn-site-title">
          <span>{{ siteName(siteRow) }}</span>
          <!-- Only when the title is the Arabic name — otherwise it already IS the
               domain, and this would print it twice. -->
          <a
            v-if="siteName(siteRow) !== siteDomain(siteRow)"
            class="rn-site-domain"
            :href="siteRow.website"
            target="_blank"
            rel="noopener noreferrer"
            dir="ltr"
          >{{ siteDomain(siteRow) }}</a>
        </v-card-title>

        <v-card-text>
          <!-- الرابط. Only the campaign link built from a stored suffix is shown. -->
          <div v-if="siteLink(siteRow)" class="rn-site-link">
            <div class="rn-site-link-label">الرابط</div>
            <div class="rn-site-link-row">
              <code class="rn-site-url" dir="ltr">{{ siteLink(siteRow) }}</code>
              <v-btn icon small title="نسخ الرابط" @click="copyUrl(siteLink(siteRow), 'الرابط')">
                <v-icon small>mdi-content-copy</v-icon>
              </v-btn>
            </div>
          </div>
          <div v-else class="rn-site-nolink">لا يوجد رابط مسجل لهذه الحملة</div>

          <!-- معلومات الحملة -->
          <div class="rn-site-facts">
            <div v-for="fact in siteFacts(siteRow)" :key="fact.label" class="rn-site-fact">
              <span class="rn-site-fact-label">{{ fact.label }}</span>
              <span class="rn-site-fact-value">{{ fact.value }}</span>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="siteDialog = false">إغلاق</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Axios from "axios";
import LatestReportFilter from "./LatestReportFilter.vue";
import { buildReferralUrl, referralSiteDomain, referralSiteName } from "@/utils/referralUrl";

export default {
  name: "ReferralChartNew",
  components: { LatestReportFilter },

  data() {
    return {
      page: 1,
      total: 0,
      loading: false,
      exportingAll: false,
      body: new FormData(),
      rows: [],
      footer: {},
      // Campaign keys whose مكرر/المجموع breakdown is open. Cleared on every fetch —
      // the keys are referral ids, which do not survive a filter change.
      expandedKeys: [],
      // تفاصيل الموقع dialog. siteRow is the row it was opened from, so the dialog
      // reads straight off the payload rather than copying fields out of it.
      siteDialog: false,
      siteRow: null,
      // What the عدد الصفوف box currently holds. Kept apart from the store so a
      // half-typed number does not refetch — see updateCounterOfShow(). Starts at
      // the default the $route watcher below resets the store to.
      tempCounterOfShow: 10,
      // This report's query is heavy (two grouped subqueries over requests and
      // collaborator_requests), so an accidental extra zero is worth catching.
      perPageMax: 500,
      // Numeric columns in RTL source order (after معلومات الحمله):
      // اول تصنيف مكتسب -> التصنيف الحالي للطلب -> باقي التصنيفات
      numericColumns: [
        { c: "first_positive_count", p: "first_positive_percentage" },
        { c: "first_negative_count", p: "first_negative_percentage" },
        { c: "first_neutral_count", p: "first_neutral_percentage" },
        { c: "positive_requests_count", p: "positive_requests_percentage" },
        { c: "negative_requests_count", p: "negative_requests_percentage" },
        { c: "current_neutral_count", p: "current_neutral_percentage" },
        { c: "send_requests_count", p: "send_requests_percentage" },
        { c: "completed_requests_count", p: "completed_requests_percentage" },
      ],
    };
  },

  computed: {
    perPage() {
      return this.$store.state.counterOfShow || 10;
    },
    pageCount() {
      return Math.max(1, Math.ceil((this.total || 0) / this.perPage));
    },
  },

  watch: {
    // Sync only — the refetch lives in updateCounterOfShow(). Refetching from
    // here meant the box could commit a new size while the table kept the old
    // one, because the fetch was gated on flags this page does not own
    // (admin_search_active is global and stays true after another screen's
    // search, and the guard against the $route reset below suppressed it too).
    "$store.state.counterOfShow": {
      handler(newVal) {
        // Keep the box in step with the store however it changed — the $route
        // watcher below resets it to 10 on entry, and other pages write to it too.
        this.tempCounterOfShow = newVal;
      },
    },
    $route: {
      immediate: true,
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.$store.state.counterOfShow = 10;
        }
      },
    },
  },

  methods: {
    num(v) {
      return v == null ? 0 : v;
    },
    pct(v) {
      return v == null ? "0%" : v;
    },
    shortName(name) {
      if (!name) return "";
      return name.length > 30 ? name.substring(0, 30) + "..." : name;
    },

    // Rows can arrive without an id, so fall back to the page index the same way
    // the :key did before the accordion was added.
    rowKey(row, idx) {
      return row.id != null ? row.id : "i" + idx;
    },
    // Only campaigns that actually have collaborator_requests can be drilled into
    // — this is the Figma's plain variant vs. its two expandable ones.
    isExpandable(row) {
      return Number(row.collaborator_total_count) > 0;
    },
    isExpanded(row) {
      return this.expandedKeys.indexOf(this.rowKey(row, -1)) > -1;
    },
    toggleRow(row) {
      const key = this.rowKey(row, -1);
      const at = this.expandedKeys.indexOf(key);
      if (at > -1) this.expandedKeys.splice(at, 1);
      else this.expandedKeys.push(key);
    },
    // The parent row IS the جديد side (collaborator_requests.type 'request'), so the
    // accordion only has to add مكرر and their المجموع. The API ships both
    // pre-computed under `breakdown`, keyed with the same field names as the parent
    // row — nothing is calculated here. The guard covers a cached response from
    // before the breakdown was reshaped.
    detailRows(row) {
      const breakdown = row.breakdown || {};
      return ["repeated", "total"]
        .filter((key) => breakdown[key])
        .map((key) => Object.assign({ key: key }, breakdown[key]));
    },

    // ---- الموقع ----
    // website is resolved server-side (referrals.website, else the source id, else a
    // legacy full URL in url1/url2) and is null when nothing identifies the site.
    // Campaigns do carry sites outside the four known ones, so fall back to the bare
    // domain rather than showing nothing for them.
    siteName(row) {
      return referralSiteName(row.website) || referralSiteDomain(row.website);
    },
    siteDomain(row) {
      return referralSiteDomain(row.website);
    },
    // One link only: the first stored suffix that builds a campaign URL.
    // buildReferralUrl joins the site prefix to the suffix, passes a legacy full URL
    // through untouched, and hands back the bare suffix for a site it has no prefix
    // for — so only absolute results count. A suffix is one path segment, so values
    // carrying whitespace (older rows keep the campaign name in url1) are not links
    // and are skipped in favour of url2.
    siteLink(row) {
      return (
        [row.url1, row.url2]
          .filter((suffix) => suffix && !/\s/.test(String(suffix)))
          .map((suffix) => buildReferralUrl(row.website, suffix))
          .find((url) => /^https?:\/\//i.test(url)) || ""
      );
    },
    siteFacts(row) {
      return [
        { label: "اسم الحملة", value: row.name || "لا يوجد" },
        { label: "البراند", value: row.brand || "لا يوجد" },
        { label: "المنصة", value: row.platform || "لا يوجد" },
        { label: "اجمالي الطلبات", value: this.num(row.collaborator_total_count) },
      ];
    },
    openSiteDialog(row) {
      this.siteRow = row;
      this.siteDialog = true;
    },
    // Same two-path copy the referrals CRUD uses: execCommand is the fallback for
    // the non-secure origins the admin is still reached over.
    copyUrl(url, label) {
      if (!url) return;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(
          () => this.showPopup("success", `تم نسخ ${label}`),
          () => this.showPopup("error", "تعذر النسخ")
        );
      } else {
        const input = document.createElement("input");
        input.value = url;
        document.body.appendChild(input);
        input.select();
        try {
          document.execCommand("copy");
          this.showPopup("success", `تم نسخ ${label}`);
        } catch (e) {
          this.showPopup("error", "تعذر النسخ");
        }
        document.body.removeChild(input);
      }
    },
    handleFilter(val) {
      this.body = new FormData();

      if (val.startDate) this.body.append("start_date", val.startDate);
      if (val.endDate) this.body.append("end_date", val.endDate);
      if (val.range) this.body.append("range", val.range);

      if (val.referral_id.length) {
        val.referral_id.forEach((item, index) => {
          // item.text is the display label (complete URL / sheet source); the API
          // filters on the raw column value.
          this.body.append([`referral_id[${index}]`], item.referralValue != null ? item.referralValue : item.text);
        });
      }
      if (val.brand.length) {
        val.brand.forEach((item, index) => {
          this.body.append([`brand[${index}]`], item.text);
        });
      }
      if (val.platform.length) {
        val.platform.forEach((item, index) => {
          this.body.append([`platform[${index}]`], item.text);
        });
      }
      if (val.dayNumber) this.body.append("range", val.dayNumber);
      if (val.is_google_sheet !== undefined && val.is_google_sheet !== null && val.is_google_sheet !== "all") {
        this.body.append("is_google_sheet", val.is_google_sheet);
      }

      this.page = 1;
      this.getTableData(1);
      this.getTableSumsData();
    },

    onPageChange(p) {
      this.page = p;
      this.getTableData(p);
    },

    // Commit the typed page size — called on blur and on Enter. An empty or
    // nonsense value falls back to the default rather than sending per_page=NaN,
    // which the API answers with an empty page; the clamped value is written back
    // so the box never disagrees with the table it is describing. The store keeps
    // the size the rest of the admin shares, and the refetch happens right here
    // so that leaving the box always redraws the table.
    updateCounterOfShow() {
      const parsed = parseInt(this.tempCounterOfShow, 10);
      const next = parsed > 0 ? Math.min(parsed, this.perPageMax) : 10;

      this.tempCounterOfShow = next;
      // Number() because other screens write the raw string from their own box.
      if (Number(this.$store.state.counterOfShow) === next) return;

      this.$store.state.counterOfShow = next;
      this.page = 1;
      this.getTableData(1);
      this.getTableSumsData();
    },

    getTableData(count) {
      this.loading = true;
      Axios.post(
        `${this.$store.state.url}/employee/admin/RequestReferralReportNew?page=${count}&per_page=${this.perPage}`,
        this.body,
        { headers: this.request_headers() }
      )
        .then((res) => {
          const payload = res.data.payload || {};
          this.rows = payload.data || [];
          this.total = payload.sum_of_resources || 0;
          this.expandedKeys = [];
          this.loading = false;
        })
        .catch(() => {
          this.rows = [];
          this.expandedKeys = [];
          this.loading = false;
        });
    },

    getTableSumsData() {
      Axios.post(
        `${this.$store.state.url}/employee/admin/RequestReferralReportNewSum`,
        this.body,
        { headers: this.request_headers() }
      ).then((res) => {
        this.footer = res.data.payload || {};
      });
    },

    exportsToExcel() {
      const table = document.querySelector(".referrals-new-classification table");
      if (!table) return;

      // exportTableToCsv serialises whatever <tr>s the table contains, so the
      // مكرر/المجموع rows would land in the sheet as ragged extra lines under each
      // campaign. Export a clone with them removed instead — cloning also means
      // v-show="false" detail rows (still in the DOM) cannot leak in either.
      const clone = table.cloneNode(true);
      clone.querySelectorAll(".rn-detail-row").forEach((tr) => tr.remove());

      this.exportTableToCsv(clone, "تقرير الروابط الديناميكية الجديد");
    },

    // exportsToExcel() only ever sees the currently rendered page, since the
    // table never holds more rows than that. This fetches every row the active
    // filter matches in one request, swaps it into the table just long enough
    // to export it, then restores whatever page the user was looking at.
    async exportAllToExcel() {
      if (this.exportingAll) return;
      this.exportingAll = true;

      const previousRows = this.rows;
      const previousExpandedKeys = this.expandedKeys;

      try {
        const res = await Axios.post(
          `${this.$store.state.url}/employee/admin/RequestReferralReportNew?page=1&per_page=${this.total || 1000000}`,
          this.body,
          { headers: this.request_headers() }
        );
        const payload = res.data.payload || {};

        this.rows = payload.data || [];
        this.expandedKeys = [];
        await this.$nextTick();

        const table = document.querySelector(".referrals-new-classification table");
        if (table) {
          const clone = table.cloneNode(true);
          clone.querySelectorAll(".rn-detail-row").forEach((tr) => tr.remove());
          this.exportTableToCsv(clone, "تقرير الروابط الديناميكية الجديد - الكل");
        }
      } finally {
        this.rows = previousRows;
        this.expandedKeys = previousExpandedKeys;
        this.exportingAll = false;
      }
    },
  },

  created() {
    this.rows = [];
  },
};
</script>

<style lang="scss">
/* ============================================================
   تقرير الروابط الديناميكية الجديد — custom grouped RTL table
   Colours pulled from Figma (light 1850-3884 / dark 1850-4647).
   Dark mode follows the app-wide `.dark` class on html/body.
   ============================================================ */
.referrals-new-report {
  /* light palette */
  --rn-group-a: #341036;      /* معلومات الحمله / اول تصنيف مكتسب */
  --rn-group-b: #451547;      /* الحالي / باقي / المجموع */
  --rn-cell-bg: #ffffff;
  --rn-cell-bg-alt: #f5f4f7;  /* alternating row (assumption: subtle plum-grey) */
  --rn-border: rgba(238, 238, 238, 0.93);
  --rn-count: #047a38;        /* green */
  --rn-pct: #451547;
  --rn-text: #451547;
  --rn-label: #9747ff;      /* kept for the toggle's focus ring */
  --rn-dot: #9747ff;
  --rn-footer-bg: #ece8ef;
  /* Accordion (Figma 1862-5068). The node only ships dark values; the light
     column is derived from the palette above. */
  --rn-toggle: #451547;         /* the ▼ / ▲ caret */

  .referrals-new-classification {
    padding: 0 12px 20px;
  }

  .rn-table-wrapper {
    width: 100%;
    overflow-x: auto;
    border-radius: 4px;
  }

  .rn-table {
    width: 100%;
    min-width: 1200px;
    border-collapse: collapse;
    table-layout: fixed;
    font-family: inherit;
  }

  /* ---------- header ---------- */
  .rn-h {
    color: #ffffff;
    font-weight: 900;
    font-size: 13px;
    text-align: center;
    letter-spacing: -0.5px;
    border: 1px solid #ffffff;
    padding: 10px;
    height: 44px;
    white-space: nowrap;
  }
  .rn-group-row .rn-h { height: 56px; }

  .rn-g-info,
  .rn-s-info,
  .rn-g-first,
  .rn-s-first { background: var(--rn-group-a); }

  .rn-g-current,
  .rn-s-current,
  .rn-g-remaining,
  .rn-s-remaining,
  .rn-g-total { background: var(--rn-group-b); }

  /* Column widths live on the <colgroup> in the template — under
     table-layout: fixed a width here would be ignored, because the first row is
     the colspan'd group header. */

  /* ---------- data cells ---------- */
  .rn-td {
    border: 1px solid var(--rn-border);
    background: var(--rn-cell-bg);
    height: 84px;
    padding: 8px;
    text-align: center;
    vertical-align: middle;
  }

  /* Zebra is driven by the DATA index, not :nth-child — the جديد/مكرر detail
     rows sit between data rows and would otherwise make every campaign odd. */
  .rn-data-row.rn-alt .rn-td { background: var(--rn-cell-bg-alt); }

  .rn-info-cell {
    color: var(--rn-text);
    font-weight: 500;
    font-size: 12px;
    letter-spacing: -0.5px;
  }
  .rn-name-cell span { display: inline-block; }

  .rn-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }
  .rn-line {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    height: 22px;
  }
  .rn-count {
    color: var(--rn-count);
    font-weight: 700;
    font-size: 12px;
  }
  .rn-pct {
    color: var(--rn-pct);
    font-weight: 700;
    font-size: 12px;
  }
  .rn-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--rn-dot);
    flex: 0 0 auto;
  }

  /* ---------- campaign cell: expand toggle ---------- */
  .rn-campaign {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  .rn-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    cursor: pointer;
  }
  .rn-toggle:focus-visible { outline: 2px solid var(--rn-label); outline-offset: 2px; }
  .rn-caret {
    flex: 0 0 auto;
    color: var(--rn-toggle);
  }
  .rn-campaign-name {
    display: inline-block;
    flex: 1 1 auto;
    text-align: center;
    color: var(--rn-text);
    font-weight: 700;
    font-size: 12px;
    letter-spacing: -0.5px;
  }

  /* ---------- الموقع cell ---------- */
  /* A link, not a button: it opens details about the row, so it should not read
     as an action on the same footing as the expand toggle. */
  .rn-site {
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    font-weight: 700;
    cursor: pointer;
    text-decoration: underline;
    text-decoration-style: dotted;
    text-underline-offset: 3px;
  }
  .rn-site:hover { text-decoration-style: solid; }
  .rn-site:focus-visible { outline: 2px solid var(--rn-label); outline-offset: 2px; }

  /* ---------- مكرر / المجموع detail rows ---------- */
  .rn-detail-row .rn-td {
    height: 45px;
    padding: 4px 10px;
    background: var(--rn-cell-bg);
  }
  .rn-detail-label {
    /* The label sits at the RTL start of a cell that spans the whole
       معلومات الحمله block, matching the Figma's justify-end. */
    text-align: start;
    color: var(--rn-text);
    font-weight: 500;
    font-size: 12px;
    letter-spacing: -0.5px;
    white-space: nowrap;
  }
  .rn-detail-row .rn-cell { gap: 2px; }
  .rn-detail-row .rn-line { height: 18px; }

  /* المجموع closes the accordion, so it reads as a subtotal rather than a third
     peer of the campaign and مكرر above it. */
  .rn-detail-total .rn-td { border-top: 1px solid var(--rn-border); }
  .rn-detail-total .rn-detail-label,
  .rn-detail-total .rn-count,
  .rn-detail-total .rn-pct { font-weight: 900; }

  /* v-show toggles the row itself; fading the inner content keeps the
     transition off the <tr>, where transforms do not apply cleanly. */
  .rn-detail-fade { animation: rn-detail-in 0.2s ease; }
  @keyframes rn-detail-in {
    from { opacity: 0; transform: translateY(-5px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* ---------- footer ---------- */
  .rn-footer-row .rn-td { background: var(--rn-footer-bg); font-weight: 700; }
  .rn-footer-label {
    color: var(--rn-text);
    font-weight: 900;
    font-size: 13px;
  }

  .rn-empty-cell {
    text-align: center;
    padding: 24px;
    color: #888;
    font-size: 14px;
    border: 1px solid var(--rn-border);
  }

  /* ---------- footer bar: page size + pagination ---------- */
  .rn-table-footer {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 16px;
  }
  .rn-per-page {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 0 0 auto;
  }
  .rn-per-page-label {
    color: var(--rn-text);
    font-size: 13px;
    font-weight: 500;
  }
  .rn-per-page-input {
    width: 60px;
    height: 35px;
    padding: 0 8px;
    border: 1px solid var(--rn-border);
    border-radius: 6px;
    background: var(--rn-cell-bg);
    color: var(--rn-text);
    font-size: 13px;
    font-weight: 700;
    text-align: center;
  }
  .rn-per-page-input:focus {
    outline: none;
    border-color: var(--rn-dot);
  }

  /* The pagination keeps the centre of the bar; the empty flex space on the far
     side balances the page-size control opposite it. */
  .rn-pagination {
    display: flex;
    justify-content: center;
    flex: 1 1 auto;
  }
}

/* ================= DARK MODE ================= */
.dark .referrals-new-report {
  --rn-cell-bg: #282334;
  --rn-cell-bg-alt: #201b2a;
  --rn-border: rgba(111, 112, 115, 0.93);
  --rn-count: #06bd59;
  --rn-pct: #ffffff;
  --rn-text: #ffffff;
  --rn-label: #cca4ff;
  --rn-dot: #cca4ff;
  --rn-footer-bg: #352f47;
  --rn-toggle: #ffffff;
}

/* ================= تفاصيل الموقع dialog =================
   v-dialog renders into .v-application, outside .referrals-new-report, so these
   cannot be nested with the table's rules above — and they carry their own
   colours rather than the --rn-* variables, which are scoped to that subtree. */
.rn-site-card {
  .rn-site-title {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    font-size: 18px;
    font-weight: 700;
  }
  .rn-site-domain {
    font-size: 12px;
    font-weight: 400;
    color: #7a5c85;
    text-decoration: none;
  }
  .rn-site-domain:hover { text-decoration: underline; }

  .rn-site-link-label {
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 4px;
  }
  .rn-site-link-row {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .rn-site-url {
    /* The link is the point of this dialog, so it wraps in full rather than
       being clipped — an ellipsised URL cannot be checked by eye. */
    flex: 1 1 auto;
    padding: 6px 8px;
    border-radius: 6px;
    background: rgba(69, 21, 71, 0.06);
    font-size: 12px;
    line-height: 1.5;
    word-break: break-all;
  }

  .rn-site-nolink {
    font-size: 13px;
    opacity: 0.7;
  }

  .rn-site-facts {
    margin-top: 18px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    padding-top: 12px;
  }
  .rn-site-fact {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 4px 0;
    font-size: 13px;
  }
  .rn-site-fact-label { opacity: 0.7; }
  .rn-site-fact-value {
    font-weight: 700;
    text-align: start;
    word-break: break-word;
  }
}

.dark .rn-site-card {
  .rn-site-domain { color: #cca4ff; }
  .rn-site-url { background: rgba(255, 255, 255, 0.08); }
  .rn-site-facts { border-top-color: rgba(255, 255, 255, 0.12); }
}
</style>
